/** @format */

import { api } from '../../../../api.ts';
import { sortBy } from '../../../../utils/sort';
import {
  buildGroupsAfterRemovingTechnician,
  buildGroupsClearPlanning,
} from './dispoDrawerDrag';
import {
  groupTechniciansPatch,
  groupToApiPayload,
  parseGroupHours,
  readGroupField,
  syncHoursFromGroups,
} from './groupSchema';
import stableCrc32Id from './planningCrc32';
import { getPlanningDefaultStart } from './planningDefaultStart';
import {
  clientProjectFromApi,
  clientProjectStatusFromApi,
  domainFromApi,
  driveFileFromApi,
  foreignBusyBlockFromApi,
  interventionLayerFromApi,
  interventionTagFromApi,
  planningConfigFromApi,
  planningSlotFromApi,
  projectFromApi,
  quickAddCatalogFromApi,
  serviceTypeFromApi,
  sidebarQueueItemFromApi,
  technicianUnavailabilityFromApi,
  tripConfirmationDetailFromApi,
  tripFromApi,
  tripLogFromApi,
} from './planningModels';
import { planningSmileyPatchFromApi } from './planningSmiley';
import { planningTimeToApi } from './technicianUnavailability';
import { addHoursToTime } from './time';

export function residualLinesFromApi(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row) => ({
      clientId: Number(row?.client_id ?? row?.clientId),
      amount: Number(row?.amount) || 0,
    }))
    .filter((row) => Number.isFinite(row.clientId));
}

/** Normalizes DRF list responses (array or paginated `{ results }`). */
export function normalizePlanningList(data) {
  if (Array.isArray(data)) return data;
  return data?.results ?? [];
}

export { syncHoursFromGroups };

/** Duration of one technician slot (e.g. 2×7h → 7h, not 14h). */
export function tripSlotDurationHours(trip) {
  const groups = trip?.groups || [];
  if (!groups.length) {
    return Number(trip?.estimatedHours) || 1;
  }
  const withFreeSlot = groups.find((group) => {
    const technicianCount =
      Number(readGroupField(group, 'technician_count')) || 1;
    const ids = readGroupField(group, 'technician_ids') || [];
    const filled = ids.filter((id) => id != null).length;
    return filled < technicianCount;
  });
  const target = withFreeSlot || groups[0];
  return Number(readGroupField(target, 'duration')) || 1;
}

/** @deprecated Use tripSlotDurationHours */
export const interventionSlotDurationHours = tripSlotDurationHours;
export const ciSlotDurationHours = tripSlotDurationHours;

/** Groups payload for PATCH/POST (canonical field names). */
export function groupsForTripPayload(groups) {
  return (groups || []).map((group, index) => groupToApiPayload(group, index));
}

export const groupsForInterventionPayload = groupsForTripPayload;

export function interventionConstraintFromApi(data) {
  return {
    id: data.id,
    label: data.label || '',
    description: data.description || '',
    isActive: data.is_active !== false,
  };
}

export function interventionConstraintStatusFromApi(data) {
  return {
    id: data.id,
    label: data.label || '',
    sortOrder: data.sort_order ?? 0,
  };
}

export function tripConstraintsPayload(trip) {
  return (trip.constraints || []).map((row) => ({
    constraint_id: row.constraintId,
    status_id: row.statusId,
  }));
}

export const interventionConstraintsPayload = tripConstraintsPayload;

export async function fetchInterventionConstraints(search = '') {
  const params = {};
  const trimmed = (search || '').trim();
  if (trimmed) params.search = trimmed;
  const response = await api.get('/planning/intervention-constraints/', params);
  return normalizePlanningList(response.data).map(
    interventionConstraintFromApi,
  );
}

export async function createInterventionConstraint(label) {
  const response = await api.post(
    '/planning/intervention-constraints/',
    {},
    { label: label.trim() },
  );
  return interventionConstraintFromApi(response.data);
}

export function tripLabelFromApi(data) {
  return {
    id: data.id,
    label: data.label || '',
  };
}

export async function fetchTripLabels(search = '') {
  const params = {};
  const trimmed = (search || '').trim();
  if (trimmed) params.search = trimmed;
  const response = await api.get('/planning/trip-labels/', params);
  return normalizePlanningList(response.data).map(tripLabelFromApi);
}

export async function createTripLabel(label) {
  const response = await api.post(
    '/planning/trip-labels/',
    {},
    { label: label.trim() },
  );
  return tripLabelFromApi(response.data);
}

export function planningSmsTemplateFromApi(data) {
  return {
    id: data.id,
    name: data.name || '',
    body: data.body || '',
    isDefault: Boolean(data.is_default),
    domainId: data.domain ?? null,
    serviceTypeId: data.service_type ?? null,
    createdDate: data.created_date || null,
    updatedDate: data.updated_date || null,
  };
}

export function planningEmailTemplateFromApi(data) {
  return {
    id: data.id,
    name: data.name || '',
    subject: data.subject || '',
    body: data.body || '',
    isDefault: Boolean(data.is_default),
    domainId: data.domain ?? null,
    serviceTypeId: data.service_type ?? null,
    createdDate: data.created_date || null,
    updatedDate: data.updated_date || null,
  };
}

async function fetchPlanningTextTemplates(
  endpoint,
  search = '',
  mapper = planningSmsTemplateFromApi,
  { domainId = null, serviceTypeId = null } = {},
) {
  const params = {};
  const trimmed = (search || '').trim();
  if (trimmed) params.search = trimmed;
  if (domainId != null) params.domain_id = domainId;
  if (serviceTypeId != null) params.service_type_id = serviceTypeId;
  const response = await api.get(endpoint, params);
  return normalizePlanningList(response.data).map(mapper);
}

async function createPlanningTextTemplate(
  endpoint,
  payload,
  mapper = planningSmsTemplateFromApi,
) {
  const response = await api.post(endpoint, {}, payload);
  return mapper(response.data);
}

export async function fetchPlanningSmsTripConfirmationTemplates(
  search = '',
  { domainId = null, serviceTypeId = null } = {},
) {
  return fetchPlanningTextTemplates(
    '/planning/sms-trip-confirmation-templates/',
    search,
    planningSmsTemplateFromApi,
    { domainId, serviceTypeId },
  );
}

export async function createPlanningSmsTripConfirmationTemplate(
  name,
  body,
  { domainId = null, serviceTypeId = null, isDefault = false } = {},
) {
  return createPlanningTextTemplate(
    '/planning/sms-trip-confirmation-templates/',
    {
      name: name.trim(),
      body: body.trim(),
      is_default: Boolean(isDefault),
      ...(domainId != null ? { domain: domainId } : {}),
      ...(serviceTypeId != null ? { service_type: serviceTypeId } : {}),
    },
  );
}

export async function fetchPlanningEmailTripConfirmationTemplates(
  search = '',
  { domainId = null, serviceTypeId = null } = {},
) {
  return fetchPlanningTextTemplates(
    '/planning/email-trip-confirmation-templates/',
    search,
    planningEmailTemplateFromApi,
    { domainId, serviceTypeId },
  );
}

export async function createPlanningEmailTripConfirmationTemplate(
  name,
  subject,
  body,
  { domainId = null, serviceTypeId = null, isDefault = false } = {},
) {
  return createPlanningTextTemplate(
    '/planning/email-trip-confirmation-templates/',
    {
      name: name.trim(),
      subject: subject.trim(),
      body: body.trim(),
      is_default: Boolean(isDefault),
      ...(domainId != null ? { domain: domainId } : {}),
      ...(serviceTypeId != null ? { service_type: serviceTypeId } : {}),
    },
    planningEmailTemplateFromApi,
  );
}

export async function fetchPlanningSmsAvailabilityTemplates(
  search = '',
  { domainId = null, serviceTypeId = null } = {},
) {
  return fetchPlanningTextTemplates(
    '/planning/sms-availability-templates/',
    search,
    planningSmsTemplateFromApi,
    { domainId, serviceTypeId },
  );
}

export async function createPlanningSmsAvailabilityTemplate(
  name,
  body,
  { domainId = null, serviceTypeId = null, isDefault = false } = {},
) {
  return createPlanningTextTemplate('/planning/sms-availability-templates/', {
    name: name.trim(),
    body: body.trim(),
    is_default: Boolean(isDefault),
    ...(domainId != null ? { domain: domainId } : {}),
    ...(serviceTypeId != null ? { service_type: serviceTypeId } : {}),
  });
}

export async function fetchPlanningEmailAvailabilityTemplates(
  search = '',
  { domainId = null, serviceTypeId = null } = {},
) {
  return fetchPlanningTextTemplates(
    '/planning/email-availability-templates/',
    search,
    planningEmailTemplateFromApi,
    { domainId, serviceTypeId },
  );
}

export async function createPlanningEmailAvailabilityTemplate(
  name,
  subject,
  body,
  { domainId = null, serviceTypeId = null, isDefault = false } = {},
) {
  return createPlanningTextTemplate(
    '/planning/email-availability-templates/',
    {
      name: name.trim(),
      subject: subject.trim(),
      body: body.trim(),
      is_default: Boolean(isDefault),
      ...(domainId != null ? { domain: domainId } : {}),
      ...(serviceTypeId != null ? { service_type: serviceTypeId } : {}),
    },
    planningEmailTemplateFromApi,
  );
}

/** @deprecated Use fetchPlanningSmsTripConfirmationTemplates */
export async function fetchPlanningSmsTemplates(search = '') {
  return fetchPlanningSmsTripConfirmationTemplates(search);
}

/** @deprecated Use createPlanningSmsTripConfirmationTemplate */
export async function createPlanningSmsTemplate(name, body) {
  return createPlanningSmsTripConfirmationTemplate(name, body);
}

export function technicianUnavailabilityReasonFromApi(data) {
  return {
    id: data.id,
    label: data.label || '',
  };
}

export async function fetchTechnicianUnavailabilityReasons(search = '') {
  const params = {};
  const trimmed = (search || '').trim();
  if (trimmed) params.search = trimmed;
  const response = await api.get(
    '/planning/technician-unavailability-reasons/',
    params,
  );
  return normalizePlanningList(response.data).map(
    technicianUnavailabilityReasonFromApi,
  );
}

export async function createTechnicianUnavailabilityReason(label) {
  const response = await api.post(
    '/planning/technician-unavailability-reasons/',
    {},
    { label: label.trim() },
  );
  return technicianUnavailabilityReasonFromApi(response.data);
}

export async function fetchInterventionConstraintStatuses() {
  const response = await api.get('/planning/intervention-constraint-statuses/');
  return normalizePlanningList(response.data).map(
    interventionConstraintStatusFromApi,
  );
}

export async function fetchTripPriorities() {
  const response = await api.get('/planning/trip-priorities/');
  return normalizePlanningList(response.data);
}

export async function fetchDomains() {
  const response = await api.get('/planning/domains/');
  return normalizePlanningList(response.data).map(domainFromApi);
}

export async function fetchTaskTemplates({ includeInactive = false } = {}) {
  const params = includeInactive ? { include_inactive: '1' } : {};
  const response = await api.get('/planning/task-templates/', params);
  return normalizePlanningList(response.data);
}

export async function fetchTaskQuantitySources() {
  const response = await api.get('/planning/task-templates/quantity-sources/');
  return normalizePlanningList(response.data);
}

export async function createTaskTemplate(payload) {
  const response = await api.post('/planning/task-templates/', {}, payload);
  return response.data;
}

export async function patchTaskTemplate(id, payload) {
  const response = await api.patch(
    `/planning/task-templates/${id}/`,
    {},
    payload,
  );
  return response.data;
}

export async function reorderTaskTemplates(ids) {
  const response = await api.post(
    '/planning/task-templates/reorder/',
    {},
    { ids },
  );
  return normalizePlanningList(response.data);
}

export function interventionTaskFromApi(data) {
  if (!data) return null;
  return {
    id: data.id == null || data.id === '' ? null : Number(data.id),
    clientKey: data.client_key || (data.id != null ? `task-${data.id}` : null),
    interventionId: data.intervention_id,
    templateId: data.template_id,
    tripId: data.trip_id ?? null,
    label: data.label || '',
    shortName: (data.short_name || data.template_short_name || '').trim(),
    durationMinutes: Number(data.duration_minutes) || 0,
    minimumTechnicianCount: Math.max(
      1,
      Number(data.minimum_technician_count) || 1,
    ),
    sequence: data.sequence ?? 0,
    packingKey: data.packing_key || '',
    mustBeAlone: Boolean(data.must_be_alone),
    isManual: Boolean(data.is_manual),
    isLocked: Boolean(data.is_locked),
    status: data.status || 'to_do',
    forcedTechnicianIds: (data.forced_technician_ids || []).map((id) =>
      Number(id),
    ),
  };
}

export async function fetchInterventionTasks(interventionId) {
  const response = await api.get('/planning/intervention-tasks/', {
    intervention: interventionId,
  });
  return normalizePlanningList(response.data)
    .map(interventionTaskFromApi)
    .filter(Boolean);
}

export async function patchInterventionTask(id, payload) {
  const response = await api.patch(
    `/planning/intervention-tasks/${id}/`,
    {},
    payload,
  );
  return interventionTaskFromApi(response.data);
}

export async function createInterventionTask(payload) {
  const response = await api.post('/planning/intervention-tasks/', {}, payload);
  return interventionTaskFromApi(response.data);
}

export async function deleteInterventionTask(id) {
  await api.delete(`/planning/intervention-tasks/${id}/`);
}

export async function previewInterventionTasks(
  interventionId,
  { fresh = false } = {},
) {
  const response = await api.post(
    `/planning/interventions/${interventionId}/tasks/preview/`,
    {},
    fresh ? { fresh: true } : {},
  );
  return normalizePlanningList(response.data)
    .map(interventionTaskFromApi)
    .filter(Boolean);
}

export async function commitInterventionTaskPlan(interventionId, payload) {
  const response = await api.post(
    `/planning/interventions/${interventionId}/tasks/commit/`,
    {},
    payload,
  );
  const data = response.data || {};
  return {
    trips: (Array.isArray(data.trips) ? data.trips : [])
      .map(tripFromApi)
      .filter(Boolean),
    tasks: (Array.isArray(data.tasks) ? data.tasks : [])
      .map(interventionTaskFromApi)
      .filter(Boolean),
  };
}

export async function distributeInterventionTasks(interventionId) {
  const response = await api.post(
    `/planning/interventions/${interventionId}/tasks/distribute/`,
    {},
    {},
  );
  const data = response.data || {};
  return {
    trips: (Array.isArray(data.trips) ? data.trips : [])
      .map(tripFromApi)
      .filter(Boolean),
    tasks: (Array.isArray(data.tasks) ? data.tasks : [])
      .map(interventionTaskFromApi)
      .filter(Boolean),
  };
}

export async function syncInterventionTasks(interventionId) {
  const response = await api.post(
    `/planning/interventions/${interventionId}/tasks/sync/`,
    {},
    {},
  );
  return normalizePlanningList(response.data)
    .map(interventionTaskFromApi)
    .filter(Boolean);
}

export async function attachTasksToTrip(tripId, taskIds) {
  const response = await api.post(
    `/planning/trips/${tripId}/tasks/`,
    {},
    { task_ids: taskIds },
  );
  return { row: tripFromApi(response.data) };
}

export async function fetchPlanningEquipment({ includeInactive = false } = {}) {
  const params = includeInactive ? { include_inactive: '1' } : {};
  const response = await api.get('/planning/equipment/', params);
  return normalizePlanningList(response.data);
}

export async function createPlanningEquipment(payload) {
  const response = await api.post('/planning/equipment/', {}, payload);
  return response.data;
}

export async function patchPlanningEquipment(id, payload) {
  const response = await api.patch(`/planning/equipment/${id}/`, {}, payload);
  return response.data;
}

export async function fetchPlanningVehicles({ includeInactive = false } = {}) {
  const params = includeInactive ? { include_inactive: '1' } : {};
  const response = await api.get('/planning/vehicles/', params);
  return normalizePlanningList(response.data);
}

export async function createPlanningVehicle(payload) {
  const response = await api.post('/planning/vehicles/', {}, payload);
  return response.data;
}

export async function patchPlanningVehicle(id, payload) {
  const response = await api.patch(`/planning/vehicles/${id}/`, {}, payload);
  return response.data;
}

export async function fetchTypologies(search = '') {
  const params = { page_size: 200 };
  if (search) params.search = search;
  const response = await api.get('/products/v2/typologies/', params);
  return normalizePlanningList(response.data);
}

export async function fetchTaskTemplateTypologyFields(typology) {
  const response = await api.get('/planning/task-templates/typology-fields/', {
    typology: typology || '',
  });
  return Array.isArray(response.data) ? response.data : [];
}

export async function patchTaskTemplateSkill(templateId, technicianId, level) {
  const response = await api.patch(
    `/planning/task-templates/${templateId}/skills/`,
    {},
    { technician_id: technicianId, level },
  );
  return response.data;
}

export async function fetchServiceTypes() {
  const response = await api.get('/planning/service-types/');
  return normalizePlanningList(response.data).map(serviceTypeFromApi);
}

export async function fetchPlanningConfig() {
  const response = await api.get('/planning/config/');
  return planningConfigFromApi(response.data);
}

export async function patchPlanningConfig(config) {
  const response = await api.patch(
    '/planning/config/',
    {},
    {
      default_start: planningTimeToApi(config?.defaultStart),
      seasonal_start_enabled: Boolean(config?.seasonalEnabled),
      seasonal_periods: (config?.periods || []).map((period) => ({
        week_start: Number(period.weekStart),
        week_end: Number(period.weekEnd),
        start: planningTimeToApi(period.start),
      })),
    },
  );
  return planningConfigFromApi(response.data);
}

/** Build the API payload from a trip config-panel draft. */
export function tripPayloadFromDraft(draft, fallbackHours = 0) {
  const estimatedHours = syncHoursFromGroups(draft.groups) || fallbackHours;
  const dateValue = draft.date?.trim() ? draft.date : null;
  return {
    label: (draft.label || '').trim(),
    date: dateValue,
    note: draft.note || '',
    priority: draft.priorityKey || 'medium',
    difficulty: draft.difficulty ?? null,
    groups: groupsForTripPayload(draft.groups),
    constraints: (draft.constraints || []).map((row) => ({
      constraint_id: row.constraintId,
      status_id: row.statusId,
    })),
    competences: draft.competences || [],
    estimated_hours: estimatedHours,
  };
}

export const interventionPayloadFromDraft = tripPayloadFromDraft;

export async function createTrip(interventionId, payload, { headers } = {}) {
  const response = await api.post(
    '/planning/trips/',
    {},
    { intervention_id: interventionId, ...payload },
    headers || {},
  );
  return { response, row: tripFromApi(response.data) };
}

export async function placeWorkItemOnPlanning({
  workItem,
  interventionId,
  date,
  groups,
  patchTripOptimistic,
  addIntervention,
  trip,
}) {
  if (workItem?.kind === 'trip' && workItem.tripId) {
    return patchTripOptimistic(
      workItem.tripId,
      { date, groups },
      { trip: trip || workItem.trip },
    );
  }
  const taskIds = (workItem.taskIds || [])
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id));
  const estimatedHours = Number(workItem.estimatedHours);
  const { runTripCreate } = await import('../sync/tripMutations');
  const { row } = await runTripCreate(interventionId, {
    date,
    groups,
    ...(taskIds.length ? { task_ids: taskIds } : {}),
    label: workItem.label,
    ...(Number.isFinite(estimatedHours)
      ? { estimated_hours: estimatedHours }
      : {}),
  });
  if (addIntervention && row) addIntervention(row);
  return { ok: true, row };
}

export const createIntervention = createTrip;

export function tripDraftForPlanningRemoval(trip, { technicianId } = {}) {
  if (technicianId != null) {
    const { groups, date } = buildGroupsAfterRemovingTechnician(
      trip,
      technicianId,
    );
    return {
      label: trip.label,
      date,
      note: trip.note || '',
      groups,
      constraints: tripConstraintsPayload(trip),
      competences: trip.competences || [],
    };
  }
  return {
    label: trip.label,
    date: '',
    note: trip.note || '',
    groups: buildGroupsClearPlanning(trip),
    constraints: tripConstraintsPayload(trip),
    competences: trip.competences || [],
  };
}

export const interventionDraftForPlanningRemoval = tripDraftForPlanningRemoval;
export const ciDraftForPlanningRemoval = tripDraftForPlanningRemoval;

/** Partial PATCH — only send changed fields; status is computed on the server. */
export async function patchTrip(tripId, patch, { headers } = {}) {
  const response = await api.patch(
    `/planning/trips/${tripId}/`,
    {},
    patch,
    headers || {},
  );
  return { response, row: tripFromApi(response.data) };
}

export async function forceTripGoogleCalendarSync(tripId) {
  const response = await api.post(
    `/planning/trips/${tripId}/sync-google-calendar/`,
    {},
    {},
  );
  return response.data;
}

export async function setTripLock(
  tripId,
  { locked, reason = '', alsoTripIds = [] } = {},
) {
  const response = await api.post(
    `/planning/trips/${tripId}/set-lock/`,
    {},
    {
      locked: Boolean(locked),
      reason: reason || '',
      also_trip_ids: alsoTripIds,
    },
  );
  return normalizePlanningList(response.data?.rows).map(tripFromApi);
}

export async function proposeAlternateTripSlots(trips) {
  const response = await api.post(
    '/planning/trips/propose-alternate-slots/',
    {},
    {
      trips: (trips || []).map((trip) => ({
        trip_id: trip.tripId,
        date: trip.date,
        groups: trip.groups,
      })),
    },
  );
  return (response.data.results || []).map((result) => ({
    row: tripFromApi(result.row),
    sourceRow: tripFromApi(result.source_row),
  }));
}

export async function proposeAlternateTripSlot(tripId) {
  const response = await api.post(
    `/planning/trips/${tripId}/propose-alternate-slot/`,
    {},
    {},
  );
  return {
    row: tripFromApi(response.data.row),
    sourceRow: tripFromApi(response.data.source_row),
  };
}

export async function fetchTripConfirmationDetail(tripId) {
  const response = await api.get(`/planning/trips/${tripId}/confirmation/`, {});
  return tripConfirmationDetailFromApi(response.data);
}

export async function sendTripConfirmationSms(tripId, body, tripIds = null) {
  const payload = { body };
  if (Array.isArray(tripIds) && tripIds.length > 0) {
    payload.trip_ids = tripIds;
  }
  await api.post(
    `/planning/trips/${tripId}/confirmation/send-sms/`,
    {},
    payload,
  );
}

export async function previewTripConfirmationSms(tripId, body, tripIds = null) {
  const payload = { body };
  if (Array.isArray(tripIds) && tripIds.length > 0) {
    payload.trip_ids = tripIds;
  }
  const response = await api.post(
    `/planning/trips/${tripId}/confirmation/preview-sms/`,
    {},
    payload,
  );
  return {
    body: response.data?.body || '',
  };
}

export async function sendTripConfirmationEmail(
  tripId,
  { subject, body, recipients, cc, tripIds = null },
) {
  const payload = { subject, body };
  if (recipients != null) payload.recipients = recipients;
  if (cc != null) payload.cc = cc;
  if (Array.isArray(tripIds) && tripIds.length > 0) {
    payload.trip_ids = tripIds;
  }
  await api.post(
    `/planning/trips/${tripId}/confirmation/send-email/`,
    {},
    payload,
  );
}

export async function previewTripConfirmationEmail(
  tripId,
  { subject, body, tripIds = null },
) {
  const payload = { subject, body };
  if (Array.isArray(tripIds) && tripIds.length > 0) {
    payload.trip_ids = tripIds;
  }
  const response = await api.post(
    `/planning/trips/${tripId}/confirmation/preview-email/`,
    {},
    payload,
  );
  return {
    subject: response.data?.subject || '',
    body: response.data?.body || '',
  };
}

export async function renewAvailabilityPortalLink(interventionId) {
  const response = await api.post(
    `planning/interventions/${interventionId}/renew_availability_portal_link/`,
    {},
    {},
  );
  return response.data?.availability_portal_url || null;
}

export async function sendAvailabilitySms(interventionId, body) {
  await api.post(
    `planning/interventions/${interventionId}/send_availability_sms/`,
    {},
    { body },
  );
}

export async function previewAvailabilitySms(interventionId, body) {
  const response = await api.post(
    `planning/interventions/${interventionId}/preview_availability_sms/`,
    {},
    { body },
  );
  return {
    body: response.data?.body || '',
  };
}

export async function sendAvailabilityEmail(
  interventionId,
  { subject, body, recipients, cc },
) {
  const payload = { subject, body };
  if (recipients != null) payload.recipients = recipients;
  if (cc != null) payload.cc = cc;
  await api.post(
    `planning/interventions/${interventionId}/send_availability_email/`,
    {},
    payload,
  );
}

export async function previewAvailabilityEmail(
  interventionId,
  { subject, body },
) {
  const response = await api.post(
    `planning/interventions/${interventionId}/preview_availability_email/`,
    {},
    { subject, body },
  );
  return {
    subject: response.data?.subject || '',
    body: response.data?.body || '',
  };
}

export async function forceAcceptTripConfirmation(tripId, comment) {
  const response = await api.post(
    `/planning/trips/${tripId}/confirmation/force-accept/`,
    {},
    { comment },
  );
  return {
    row: tripFromApi(response.data),
    deletedSourceTripId: response.data.deleted_source_trip_id ?? null,
  };
}

export async function unforceAcceptTripConfirmation(tripId) {
  const response = await api.post(
    `/planning/trips/${tripId}/confirmation/unforce-accept/`,
    {},
    {},
  );
  return { row: tripFromApi(response.data) };
}

export async function recordTripConfirmationPhoneOutcome(tripId, outcome) {
  const response = await api.post(
    `/planning/trips/${tripId}/confirmation/phone-outcome/`,
    {},
    { outcome },
  );
  return {
    row: tripFromApi(response.data),
    deletedSourceTripId: response.data.deleted_source_trip_id ?? null,
  };
}

export async function forceClientAvailability(tripId, comment) {
  const response = await api.post(
    `/planning/trips/${tripId}/force-client-availability/`,
    {},
    { comment: (comment || '').trim() },
  );
  return { row: tripFromApi(response.data) };
}

export async function unforceClientAvailability(tripId, comment) {
  const response = await api.post(
    `/planning/trips/${tripId}/unforce-client-availability/`,
    {},
    { comment: (comment || '').trim() },
  );
  return { row: tripFromApi(response.data) };
}

export async function addAvailabilityEventNote(interventionId, comment) {
  await api.post(
    `planning/interventions/${interventionId}/availability_note/`,
    {},
    { comment: (comment || '').trim() },
  );
}

export async function addTripConfirmationEventNote(tripId, comment) {
  await api.post(
    `/planning/trips/${tripId}/confirmation/add-note/`,
    {},
    { comment: (comment || '').trim() },
  );
}

function tripConfirmationBundleResponseFromApi(data) {
  const payload = data || {};
  const bundledRows = (payload.bundled_rows || []).map(tripFromApi);
  return {
    row: tripFromApi(payload.row ?? payload),
    bundledRows,
    bundledTripIds: payload.bundled_trip_ids || [],
  };
}

export async function saveTripConfirmationArrivalDisplay(
  tripId,
  arrivalTimeDisplay,
) {
  const response = await api.post(
    `/planning/trips/${tripId}/confirmation/set-arrival-display/`,
    {},
    { arrival_time_display: arrivalTimeDisplay },
  );
  return { row: tripFromApi(response.data) };
}

export async function saveTripConfirmationBundle(tripId, tripIds = null) {
  const payload = {};
  if (Array.isArray(tripIds) && tripIds.length > 0) {
    payload.trip_ids = tripIds;
  }
  const response = await api.post(
    `/planning/trips/${tripId}/confirmation/set-bundle/`,
    {},
    payload,
  );
  return tripConfirmationBundleResponseFromApi(response.data);
}

export async function ensureTripConfirmationLink(tripId, tripIds = null) {
  const payload = {};
  if (Array.isArray(tripIds) && tripIds.length > 0) {
    payload.trip_ids = tripIds;
  }
  const response = await api.post(
    `/planning/trips/${tripId}/confirmation/ensure-link/`,
    {},
    payload,
  );
  return tripConfirmationBundleResponseFromApi(response.data);
}

export const patchIntervention = patchTrip;

/** Clear planning on every trip of an intervention layer (single request). */
export async function clearInterventionLayerPlanning(interventionLayerId) {
  const response = await api.post(
    `/planning/interventions/${interventionLayerId}/clear-planning/`,
    {},
    {},
  );
  return normalizePlanningList(response.data?.trips || []).map(tripFromApi);
}

/** Add a manual tag on a prestation. */
export async function addInterventionTag(
  interventionLayerId,
  { label, color },
) {
  const response = await api.post(
    `/planning/interventions/${interventionLayerId}/tags/`,
    {},
    { label, color },
  );
  return interventionTagFromApi(response.data);
}

/** Remove a manual tag, or dismiss an automatic one. */
export async function deleteInterventionTag(interventionLayerId, tagId) {
  await api.delete(
    `/planning/interventions/${interventionLayerId}/tags/${tagId}/`,
  );
}

/** Recompute the automatic tags from the client's sold products and qualification. */
export async function syncInterventionTags(interventionLayerId) {
  const response = await api.post(
    `/planning/interventions/${interventionLayerId}/tags/sync/`,
    {},
    {},
  );
  return normalizePlanningList(response.data).map(interventionTagFromApi);
}

/** @deprecated Prefer patchTrip with only the fields that changed. */
export async function persistTrip(tripId, payload) {
  return patchTrip(tripId, payload);
}

export const persistIntervention = persistTrip;

export async function deleteTrip(tripId, { headers } = {}) {
  try {
    return await api.delete(`/planning/trips/${tripId}/`, {}, headers || {});
  } catch (err) {
    // 204 No Content has an empty body; some JSON parsers reject that as failure.
    const status = err?.response?.status;
    if (status === 204 || status === 200) return err.response;
    throw err;
  }
}

export const deleteIntervention = deleteTrip;

/** Load trips, optionally filtered by intervention or project. */
export async function fetchTrips({ projectId, interventionId } = {}) {
  const params = {};
  if (projectId != null) params.project_id = projectId;
  if (interventionId != null) params.intervention_id = interventionId;
  const response = await api.get('/planning/trips/', params);
  return normalizePlanningList(response.data).map(tripFromApi);
}

export async function fetchTripLogs({ siteId, search, page } = {}) {
  const params = {};
  if (siteId != null) params.site_id = siteId;
  if (search) params.search = search;
  if (page) params.page = page;
  const response = await api.get('/planning/trip-logs/', params);
  const data = response.data || {};
  return {
    results: normalizePlanningList(data).map(tripLogFromApi),
    count: Number(data.count) || 0,
    hasMore: Boolean(data.next),
  };
}

/** Load a single trip row (used for live WebSocket refresh). */
export async function fetchTripById(tripId) {
  const response = await api.get(`/planning/trips/${tripId}/`, {});
  return tripFromApi(normalizePlanningList([response.data])[0]);
}

export async function fetchProjects({ leadId } = {}) {
  const params = {};
  if (leadId != null) params.lead_id = leadId;
  const response = await api.get('/planning/projects/', params);
  return normalizePlanningList(response.data).map(projectFromApi);
}

/** First page of the planning top-bar search. */
export const PLANNING_PROJECT_SEARCH_PAGE_SIZE = 25;

/** Lightweight project search for the planning top bar (first 25 matches). */
export async function searchPlanningProjects(search) {
  const trimmed = (search || '').trim();
  if (!trimmed) return [];
  const response = await api.get('/planning/projects/', {
    search: trimmed,
    limit: PLANNING_PROJECT_SEARCH_PAGE_SIZE,
  });
  return normalizePlanningList(response.data)
    .map(projectFromApi)
    .slice(0, PLANNING_PROJECT_SEARCH_PAGE_SIZE);
}

/**
 * Lightweight search-shaped project rows by id (includes incoming_trips).
 * Order of `ids` is preserved.
 */
export async function fetchPlanningProjectSearchRowsByIds(ids) {
  const uniqueIds = [
    ...new Set(
      (ids || [])
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0),
    ),
  ];
  if (!uniqueIds.length) return [];
  const response = await api.get('/planning/projects/', {
    ids: uniqueIds.join(','),
  });
  const rows = normalizePlanningList(response.data).map(projectFromApi);
  const byId = new Map(rows.map((row) => [Number(row.id), row]));
  return uniqueIds.map((id) => byId.get(id)).filter(Boolean);
}

const EMPTY_QUICK_ADD_CATALOG = {
  projects: [],
  trips: [],
  workItems: [],
  layers: [],
  counts: { byServiceType: {} },
};

/** Quick-add catalog; `tab` is `map` (carte) or `list` (liste). */
export async function fetchQuickAddCatalog(
  tab = 'map',
  { serviceTypeId = null, serviceTypeIds = null } = {},
) {
  const params = { tab };
  let ids = [];
  if (serviceTypeIds != null) {
    ids = serviceTypeIds;
  } else if (serviceTypeId != null) {
    ids = [serviceTypeId];
  }
  if (ids.length) {
    params.service_type = ids.map(String).join(',');
  }
  const response = await api.get('/planning/quick-add-catalog/', params);
  return quickAddCatalogFromApi(response.data || {});
}

export { EMPTY_QUICK_ADD_CATALOG };

export const SIDEBAR_QUEUE_PAGE_SIZE = 50;

const EMPTY_SIDEBAR_COUNTS = {
  total: 0,
  verified: 0,
  unverified: 0,
  byServiceType: {},
};

/**
 * Sidebar: paginated intervention layers (status not done) with server-side
 * search / verification / sub-domain filtering and global filter counts.
 * Returns `{ items, count, counts }`.
 */
export async function fetchPlanningSidebarQueue(
  {
    offset = 0,
    limit = SIDEBAR_QUEUE_PAGE_SIZE,
    search = '',
    verification = 'all',
    serviceTypeId = null,
  } = {},
  axiosConfig = {},
) {
  const params = { offset, limit, verification };
  const trimmedSearch = (search || '').trim();
  if (trimmedSearch) params.search = trimmedSearch;
  if (serviceTypeId != null) params.service_type = serviceTypeId;

  const response = await api.get(
    '/planning/sidebar-queue/',
    params,
    {},
    'json',
    axiosConfig,
  );
  const data = response.data || {};
  const rawCounts = data.counts || {};
  return {
    items: normalizePlanningList(data.results || data.interventions || []).map(
      sidebarQueueItemFromApi,
    ),
    count: Number(data.count) || 0,
    counts: {
      total: Number(rawCounts.total) || 0,
      verified: Number(rawCounts.verified) || 0,
      unverified: Number(rawCounts.unverified) || 0,
      byServiceType: rawCounts.by_service_type || {},
    },
  };
}

export { EMPTY_SIDEBAR_COUNTS };

/**
 * Intervention layers for the « À traiter » action queue dialog.
 * Includes follow-up flags; scoped trip rows for unplanned queue entries.
 */
export async function fetchPlanningLayersCatalog(axiosConfig = {}) {
  const response = await api.get(
    '/planning/layers-catalog/',
    {},
    {},
    'json',
    axiosConfig,
  );
  const data = response.data || {};
  return normalizePlanningList(data.interventions || []).map(
    sidebarQueueItemFromApi,
  );
}

export async function fetchProject(projectId) {
  const response = await api.get(`/planning/projects/${projectId}/`);
  return projectFromApi(response.data);
}

export async function patchProjectDepotTravelMinutes(
  projectId,
  depotTravelMinutes,
) {
  const response = await api.patch(
    `/planning/projects/${projectId}/`,
    {},
    { depot_travel_minutes: depotTravelMinutes },
  );
  return projectFromApi(response.data);
}

/**
 * Look up cached driving durations for a batch of legs.
 * @param {Array<{origin_lat:number,origin_lng:number,dest_lat:number,dest_lng:number,bucket:number}>} legs
 * @returns {Promise<Record<string, { durationSeconds: number, distanceMeters: number, source?: string }>>}
 */
export async function lookupTravelTimes(legs) {
  if (!legs?.length) return {};
  const response = await api.post(
    '/planning/travel-times/lookup/',
    {},
    { legs },
  );
  return response.data?.results ?? {};
}

/**
 * Persist newly computed driving durations for reuse across users/sessions.
 * @param {Array<{origin_lat:number,origin_lng:number,dest_lat:number,dest_lng:number,bucket:number,duration_seconds:number,distance_meters:number,source?:string}>} entries
 */
export async function storeTravelTimes(entries) {
  if (!entries?.length) return 0;
  const response = await api.post(
    '/planning/travel-times/store/',
    {},
    { entries },
  );
  return response.data?.stored ?? 0;
}

export async function patchProjectPlanningSmiley(projectId, planningSmiley) {
  const response = await api.patch(
    `/planning/projects/${projectId}/`,
    {},
    { planning_smiley: planningSmiley },
  );
  return planningSmileyPatchFromApi(response.data, planningSmiley);
}

export async function fetchProjectSiteDetails(projectId) {
  const response = await api.get(
    `/planning/projects/${projectId}/site-details/`,
  );
  return response.data;
}

export async function patchProjectSiteNote(projectId, planningNote) {
  const response = await api.patch(
    `/planning/projects/${projectId}/site-details/`,
    {},
    { planning_note: planningNote },
  );
  return response.data;
}

/**
 * Flatten + sort like ProductSummaryTooltip (parseProducts + sortBy('order')).
 * Expects parent lines from GET /quotes/v2/client-quote-lines/ (domain order).
 */
export function parseAndSortSoldProductsFromQuoteLines(productLines) {
  const parsedProducts = [];
  (productLines || []).forEach((productLine) => {
    parsedProducts.push(productLine);
    if (productLine.kit_lines) {
      productLine.kit_lines.forEach((kitLine) => {
        parsedProducts.push({
          ...kitLine,
          is_kit_line: true,
        });
      });
    }
  });
  return [...parsedProducts].sort(sortBy('order'));
}

/** Labels for sold-material rows in planning panels. */
export function soldMaterialLineLabels(line) {
  const product = line?.product || {};
  const prefixed = (
    product.prefixed_internal_name ||
    // API search index field
    // eslint-disable-next-line no-underscore-dangle
    product._indexed_prefixed_internal_name ||
    product.custom_internal_name ||
    product.internal_name ||
    ''
  ).trim();
  const title = (line?.title || '').trim();
  const publicName = (product.public_name || '').trim();

  return {
    primary: prefixed || title || publicName || 'Produit',
    secondary: null,
  };
}

/** @deprecated Use parseAndSortSoldProductsFromQuoteLines */
export const flattenClientQuoteProductLines =
  parseAndSortSoldProductsFromQuoteLines;

export function leadSiteGoogleDriveUrl(folderId) {
  if (!folderId) return null;
  return `https://drive.google.com/drive/u/0/folders/${folderId}`;
}

export const clientProjectGoogleDriveUrl = leadSiteGoogleDriveUrl;

/** Projects (the layer between the site and its prestations) of one site. */
export async function fetchClientProjects(siteId) {
  const response = await api.get('/planning/client-projects/', {
    site_id: siteId,
  });
  return normalizePlanningList(response.data).map(clientProjectFromApi);
}

export async function fetchClientProjectStatuses() {
  const response = await api.get('/planning/client-project-statuses/');
  return normalizePlanningList(response.data).map(clientProjectStatusFromApi);
}

export async function patchClientProject(projectId, patch) {
  const response = await api.patch(
    `/planning/client-projects/${projectId}/`,
    {},
    patch,
  );
  return clientProjectFromApi(response.data);
}

/** Create the project's Google Drive sub-folder inside the site folder. */
export async function createClientProjectDriveFolder(projectId) {
  const response = await api.post(
    `/planning/client-projects/${projectId}/create_drive_folder/`,
    {},
    {},
  );
  return clientProjectFromApi(response.data);
}

function driveFilesPayloadFromApi(data) {
  const projects = {};
  Object.entries(data?.projects || {}).forEach(([projectId, rows]) => {
    projects[String(projectId)] = (rows || []).map(driveFileFromApi);
  });
  const interventions = {};
  Object.entries(data?.interventions || {}).forEach(
    ([interventionId, rows]) => {
      interventions[String(interventionId)] = (rows || []).map(
        driveFileFromApi,
      );
    },
  );
  return {
    site: (data?.site || []).map(driveFileFromApi),
    projects,
    interventions,
    projectGoogleFolderIds: data?.project_google_folder_ids || {},
    interventionGoogleFolderIds: data?.intervention_google_folder_ids || {},
    syncError: data?.sync_error || '',
  };
}

/**
 * Drive files of a site grouped by area
 * ({ site, projects: { [id]: [] }, interventions: { [id]: [] } }).
 * Pass sync=false to read our cached rows without calling Google.
 */
export async function fetchSiteDriveFiles(siteId, { sync = true } = {}) {
  const params = { site_id: siteId };
  if (!sync) params.sync = 'false';
  const response = await api.get('/planning/site-drive-files/', params);
  return driveFilesPayloadFromApi(response.data);
}

/** Original PDF bytes, so the preview dialog can render the file inline. */
export async function fetchSiteDriveFileContent(fileId) {
  const response = await api.download(
    `/planning/site-drive-files/${fileId}/content/`,
  );
  return response.data;
}

export async function patchSiteDriveFileHidden(fileId, isHidden) {
  const response = await api.patch(
    `/planning/site-drive-files/${fileId}/`,
    {},
    { is_hidden: Boolean(isHidden) },
  );
  return driveFileFromApi(response.data);
}

/**
 * Move files between areas and persist their order.
 * `items` is [{ id, clientId, interventionId, position }] —
 * interventionId set = prestation folder; clientId without intervention =
 * leftover project folder; both null = site root.
 */
export async function reorderSiteDriveFiles(siteId, items) {
  const response = await api.post(
    '/planning/site-drive-files/reorder/',
    {},
    {
      site_id: siteId,
      items: (items || []).map((item) => ({
        id: item.id,
        client_id: item.clientId ?? item.clientProjectId ?? null,
        intervention_id: item.interventionId ?? null,
        position: item.position ?? 0,
      })),
    },
  );
  return driveFilesPayloadFromApi(response.data);
}

/** Create the prestation's Google Drive folder (in the site, then placed if attached). */
export async function createInterventionDriveFolder(interventionId) {
  const response = await api.post(
    `/planning/interventions/${interventionId}/create_drive_folder/`,
    {},
    {},
  );
  return interventionLayerFromApi(response.data);
}

/**
 * Product lines from the client's final quote (sold material).
 * Returns { lines, isV1 } — v1 quotes use /clients-quote-products/ like ProductSummaryTooltip.
 */
export async function fetchClientFinalQuoteV2ProductLines(clientId) {
  const clientRes = await api.get(`/clients/${clientId}/`);
  const finalQuote = clientRes.data?.final_quote;
  if (!finalQuote?.id) {
    return { lines: [], isV1: false };
  }

  if (finalQuote.is_v1) {
    const productsIds = (finalQuote.products || [])
      .map((product) => (typeof product === 'object' ? product.id : product))
      .filter((id) => id != null);
    if (!productsIds.length) {
      return { lines: [], isV1: true };
    }

    const linesRes = await api.get(
      `/clients-quote-products/?id__in=${productsIds.join(',')}`,
    );
    const quoteLines = normalizePlanningList(linesRes.data);
    return {
      lines: parseAndSortSoldProductsFromQuoteLines(quoteLines),
      isV1: true,
    };
  }

  const quoteRes = await api.get(`/quotes/v2/client-quotes/${finalQuote.id}/`);
  const products = quoteRes.data?.products || [];
  const productsIds = products
    .map((product) => product.id)
    .filter((id) => id != null);
  if (!productsIds.length) {
    return { lines: [], isV1: false };
  }

  const linesRes = await api.get(
    `/quotes/v2/client-quote-lines/?id__in=${productsIds.join(',')}`,
  );
  const quoteLines = normalizePlanningList(linesRes.data);
  return {
    lines: parseAndSortSoldProductsFromQuoteLines(quoteLines),
    isV1: false,
  };
}

export async function createProject({ leadId, siteId = null, label = '' }) {
  const data = { lead_id: leadId, label };
  if (siteId != null) data.site_id = siteId;
  const response = await api.post('/planning/projects/', {}, data);
  return projectFromApi(response.data);
}

export async function fetchInterventions({
  projectId,
  domainId,
  needToBeFollowedUp,
} = {}) {
  const params = {};
  if (projectId != null) params.project_id = projectId;
  if (domainId != null) params.domain_id = domainId;
  if (needToBeFollowedUp != null) {
    params.need_to_be_followed_up = needToBeFollowedUp ? 'true' : 'false';
  }
  const response = await api.get('/planning/interventions/', params);
  return normalizePlanningList(response.data).map(interventionLayerFromApi);
}

export async function fetchInterventionsNeedingDispoFollowUp() {
  return fetchInterventions({ needToBeFollowedUp: true });
}

export async function createInterventionLayer({
  projectId,
  domainId,
  serviceTypeId = null,
  label = '',
  description = '',
  subject = '',
  siteId = null,
  clientId = null,
  clientProjectId = null,
}) {
  // Chantier id is now the LeadSite id; site_id is the anchor, project_id kept as alias.
  const resolvedSiteId = siteId ?? projectId ?? null;
  const folderClientId = clientId ?? clientProjectId ?? null;
  const response = await api.post(
    '/planning/interventions/',
    {},
    {
      ...(resolvedSiteId != null ? { site_id: resolvedSiteId } : {}),
      project_id: projectId,
      domain_id: domainId,
      ...(serviceTypeId != null ? { service_type_id: serviceTypeId } : {}),
      client_id: folderClientId ?? null,
      label,
      description,
      subject,
    },
  );
  return interventionLayerFromApi(response.data);
}

export async function patchInterventionLayer(interventionId, patch) {
  const response = await api.patch(
    `/planning/interventions/${interventionId}/`,
    {},
    patch,
  );
  return interventionLayerFromApi(response.data);
}

/** Cancel a SAV/VT prestation. The row stays, with a cancelled status. */
export async function cancelInterventionLayer(interventionId) {
  const response = await api.post(
    `/planning/interventions/${interventionId}/cancel/`,
    {},
    {},
  );
  return interventionLayerFromApi(response.data);
}

/** Trips of a single project (used by ProjectPopup). */
export async function fetchProjectTrips(projectId) {
  return fetchTrips({ projectId });
}

/** @deprecated Use fetchProjectTrips */
export const fetchChantierInterventions = fetchProjectTrips;

export { groupTechniciansPatch };

/** PATCH payload: technician slots for each group (stable ids). */
export function groupsTechniciansPatch(groups) {
  return (groups || []).map((group, index) =>
    groupTechniciansPatch(group, index),
  );
}

export const planningEntryApiToRow = planningSlotFromApi;

/** Row for the “Non synchro. BC” dialog (trip + project display + schedule bounds). */
export function unsyncedBigchangeTripFromApi(data) {
  const project = data.project
    ? projectFromApi({ ...data.project, interventions: [] })
    : null;
  return {
    trip: tripFromApi(data.trip),
    project,
    startTime: data.start_time || '',
    endTime: data.end_time || '',
    technicianNames: data.technician_names || [],
  };
}

export async function fetchUnsyncedBigchangeTrips({ from, to } = {}) {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;
  const response = await api.get('/planning/unsynced-trips/', params);
  const payload = response.data || {};
  const items = normalizePlanningList(payload.items || []).map(
    unsyncedBigchangeTripFromApi,
  );
  return { items, total: payload.total ?? items.length };
}

export function actionQueueCountsFromApi(raw) {
  if (!raw || typeof raw !== 'object') {
    return { total: 0, byFilter: {} };
  }
  const byFilter = {};
  const source = raw.by_filter || {};
  Object.entries(source).forEach(([filterId, value]) => {
    byFilter[filterId] = Number(value) || 0;
  });
  return {
    total: Number(raw.total) || 0,
    byFilter,
  };
}

const dayAvailableTechnicianCountCache = new Map();
const dayAvailableTechnicianCountListeners = new Set();
let dayAvailableTechnicianCountRevision = 0;

export function dayAvailableTechnicianCountCacheKey(from, to, excludeTripId) {
  return `${from}|${to}|${excludeTripId ?? ''}`;
}

/** Drop cached day counts and notify open date pickers to reload. */
export function invalidateDayAvailableTechnicianCounts() {
  dayAvailableTechnicianCountRevision += 1;
  dayAvailableTechnicianCountCache.clear();
  dayAvailableTechnicianCountListeners.forEach((listener) => {
    listener(dayAvailableTechnicianCountRevision);
  });
}

export function subscribeDayAvailableTechnicianCounts(listener) {
  dayAvailableTechnicianCountListeners.add(listener);
  return () => dayAvailableTechnicianCountListeners.delete(listener);
}

/** Available planning technicians per day. Cached until the planning grid reloads. */
export function fetchDayAvailableTechnicianCounts(from, to, excludeTripId) {
  const key = dayAvailableTechnicianCountCacheKey(from, to, excludeTripId);
  const requestRevision = dayAvailableTechnicianCountRevision;
  const cached = dayAvailableTechnicianCountCache.get(key);
  if (cached) return Promise.resolve(cached).then((value) => value);
  const params = { from, to };
  if (excludeTripId != null && excludeTripId !== '') {
    params.exclude_trip = excludeTripId;
  }
  const pending = api
    .get('/planning/day-available-technicians/', params)
    .then((response) => {
      const counts = response.data?.counts || {};
      if (requestRevision === dayAvailableTechnicianCountRevision) {
        dayAvailableTechnicianCountCache.set(key, counts);
      }
      return counts;
    })
    .catch((error) => {
      if (dayAvailableTechnicianCountCache.get(key) === pending) {
        dayAvailableTechnicianCountCache.delete(key);
      }
      throw error;
    });
  if (requestRevision === dayAvailableTechnicianCountRevision) {
    dayAvailableTechnicianCountCache.set(key, pending);
  }
  return pending;
}

export async function fetchPlanningSchedule(from, to, { signal } = {}) {
  const response = await api.get(
    '/planning/schedule/',
    { from, to },
    {},
    'json',
    signal ? { signal } : {},
  );
  const data = response.data || {};
  const rawEntries = Array.isArray(data) ? data : data.entries || [];
  return {
    slots: normalizePlanningList(rawEntries).map(planningSlotFromApi),
    trips: normalizePlanningList(data.trips || []).map(tripFromApi),
    projects: normalizePlanningList(data.projects || []).map(projectFromApi),
    interventionLayers: normalizePlanningList(
      data.intervention_layers || [],
    ).map(interventionLayerFromApi),
    config: planningConfigFromApi(data.config || {}),
    actionQueueCounts: actionQueueCountsFromApi(data.action_queue_counts),
    plannedInterventionsTotalHt:
      Number(data.planned_interventions_total_ht) || 0,
    plannedInterventionsTotalResidual:
      Number(data.planned_interventions_total_residual) || 0,
    plannedInterventionsTotalResidualLines: residualLinesFromApi(
      data.planned_interventions_total_residual_lines,
    ),
    foreignBusyBlocks: normalizePlanningList(
      data.foreign_busy_blocks || [],
    ).map(foreignBusyBlockFromApi),
    guestTechnicians: normalizePlanningList(data.guest_technicians || []).map(
      guestTechnicianFromApi,
    ),
  };
}

/** Grid technicians busy on other companies' trips (masked « Occupé » blocks). */
export async function fetchForeignBusyBlocks(from, to) {
  const response = await api.get('/planning/schedule/foreign-busy/', {
    from,
    to,
  });
  return normalizePlanningList(response.data?.foreign_busy_blocks || []).map(
    foreignBusyBlockFromApi,
  );
}

export async function fetchTechnicianUnavailabilities({
  from,
  to,
  technicianId,
} = {}) {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;
  if (technicianId != null) params.technician_id = technicianId;
  const response = await api.get(
    '/planning/technician-unavailabilities/',
    params,
  );
  return normalizePlanningList(response.data).map(
    technicianUnavailabilityFromApi,
  );
}

export function technicianUnavailabilityPayloadFromForm(form) {
  const payload = {
    technician_id: form.technicianId,
    start_date: form.startDate,
    end_date: form.endDate,
    start_time: planningTimeToApi(form.startTime),
    end_time: planningTimeToApi(form.endTime),
    whole_day: Boolean(form.wholeDay),
    reason: (form.reason || '').trim(),
    note: (form.note || '').trim(),
  };
  if (form.kind) payload.kind = form.kind;
  return payload;
}

export function normalizeTechnicianUnavailabilityCreateResponse(data) {
  if (data?.adjusted) {
    return {
      adjusted: true,
      intent: data.intent,
      records: (data.records || []).map(technicianUnavailabilityFromApi),
      deletedIds: data.deleted_ids || [],
    };
  }
  return {
    adjusted: false,
    record: technicianUnavailabilityFromApi(data),
  };
}

export async function createTechnicianUnavailability(form) {
  const response = await api.post(
    '/planning/technician-unavailabilities/',
    {},
    technicianUnavailabilityPayloadFromForm(form),
  );
  return normalizeTechnicianUnavailabilityCreateResponse(response.data);
}

export async function makeTechnicianDayAvailable({ technicianId, date }) {
  const response = await api.post(
    '/planning/technician-unavailabilities/make-day-available/',
    {},
    {
      technician_id: technicianId,
      date,
    },
  );
  return normalizeTechnicianUnavailabilityCreateResponse(response.data);
}

export async function createTechnicianUnavailabilitiesBulk({
  technicianIds,
  startDate,
  endDate,
  startTime,
  endTime,
  wholeDay = false,
  reason,
  note,
}) {
  const response = await api.post(
    '/planning/technician-unavailabilities/bulk-create/',
    {},
    {
      technician_ids: technicianIds,
      start_date: startDate,
      end_date: endDate,
      start_time: planningTimeToApi(startTime),
      end_time: planningTimeToApi(endTime),
      whole_day: Boolean(wholeDay),
      reason: (reason || '').trim(),
      note: (note || '').trim(),
    },
  );
  return normalizePlanningList(response.data).map(
    technicianUnavailabilityFromApi,
  );
}

export async function patchTechnicianUnavailability(id, form) {
  const response = await api.patch(
    `/planning/technician-unavailabilities/${id}/`,
    {},
    technicianUnavailabilityPayloadFromForm(form),
  );
  return technicianUnavailabilityFromApi(response.data);
}

export async function deleteTechnicianUnavailability(id) {
  await api.delete(`/planning/technician-unavailabilities/${id}/`);
}

/** Carve a window out of overlapping unavailability rows, splitting multi-day absences. */
export async function excludeTechnicianUnavailability(form) {
  const response = await api.post(
    '/planning/technician-unavailabilities/exclude/',
    {},
    technicianUnavailabilityPayloadFromForm(form),
  );
  return normalizeTechnicianUnavailabilityCreateResponse(response.data);
}

/** Dispo drawer: projects per weekday (availability + type/status filters on server). */
export async function fetchPlanningDispoDrawer(from, to, options = {}) {
  const {
    type,
    status,
    advancePaid,
    newChantierOnly,
    search,
    signal,
    ...axiosRest
  } = options;
  const params = { from, to };
  if (type) params.type = type;
  if (status) params.status = status;
  if (advancePaid) params.advance_paid = 'true';
  if (newChantierOnly) params.new_chantier_only = 'true';
  const trimmedSearch = (search || '').trim();
  if (trimmedSearch) params.search = trimmedSearch;
  const response = await api.get(
    '/planning/dispo-drawer/',
    params,
    {},
    'json',
    { signal, ...axiosRest },
  );
  return response.data;
}

export function tripHasAssignedTechnician(trip) {
  return (trip?.groups || []).some((group) =>
    (readGroupField(group, 'technician_ids') || []).some((id) => id != null),
  );
}

export const interventionHasAssignedTechnician = tripHasAssignedTechnician;

/** Stable grid slot id (matches api/planning/trip_save._schedule_entry_id). */
export function scheduleEntryId(tripId, groupKey, technicianId) {
  return stableCrc32Id(`${tripId}:${groupKey}:${technicianId}`);
}

/** Build week-grid slots from a persisted trip row. */
export function planningSlotsFromTrip(trip) {
  if (!trip?.id || !trip?.date || trip.isImportedFromBigchange) return [];
  return (trip.groups || []).flatMap((group, groupIndex) => {
    const startTime =
      readGroupField(group, 'start_time') || getPlanningDefaultStart(trip.date);
    const durationH = parseGroupHours(readGroupField(group, 'duration')) || 0;
    const endTime =
      readGroupField(group, 'end_time') ||
      addHoursToTime(startTime, durationH || 1);
    const groupKey = String(readGroupField(group, 'id') ?? groupIndex);
    return (readGroupField(group, 'technician_ids') || [])
      .map((techId) => (techId == null ? null : Number(techId)))
      .filter(
        (technicianId) => technicianId != null && !Number.isNaN(technicianId),
      )
      .map((technicianId) => ({
        id: scheduleEntryId(trip.id, groupKey, technicianId),
        tripId: trip.id,
        interventionLayerId: trip.interventionId,
        interventionId: trip.id,
        projectId: trip.projectId ?? trip.chantierId ?? null,
        technicianId,
        date: trip.date,
        startTime,
        endTime,
        note: trip.note || '',
      }));
  });
}

export const planningSlotsFromIntervention = planningSlotsFromTrip;

/** Replace local slots for one trip (after PATCH / drop). */
/** Rebuild trip-backed grid slots from displayed trips. Keep annex-visit slots. */
export function rebuildPlanningFromDisplayedTrips(
  existing,
  trips,
  dragPreview = null,
) {
  const kept = (existing || []).filter(
    (slot) => slot.afterSalesVisitId || slot.technicalVisitId,
  );
  let slots = [...kept, ...(trips || []).flatMap(planningSlotsFromTrip)];
  if (dragPreview?.planId != null) {
    slots = slots.map((slot) =>
      Number(slot.id) === Number(dragPreview.planId)
        ? {
            ...slot,
            startTime: dragPreview.startTime ?? slot.startTime,
            endTime: dragPreview.endTime ?? slot.endTime,
          }
        : slot,
    );
  }
  return slots;
}

export function mergeTripSlotsIntoPlanning(planning, trip) {
  const without = (planning || []).filter(
    (slot) =>
      slot.tripId === null ||
      slot.tripId === undefined ||
      slot.tripId !== trip.id,
  );
  return [...without, ...planningSlotsFromTrip(trip)];
}

export const mergeInterventionSlotsIntoPlanning = mergeTripSlotsIntoPlanning;

function findTripForSlot(trips, slot) {
  const tripId = slot?.tripId ?? slot?.interventionId;
  if (tripId == null) return null;
  return (trips || []).find((row) => Number(row.id) === Number(tripId)) ?? null;
}

/** Trip row for a week-grid slot (context `interventions` holds trips). */
export function findTripForPlanningSlot(slot, trips) {
  return findTripForSlot(trips, slot);
}

/** Project/chantier card for a trip. */
export function findProjectForTrip(trip, projects) {
  const projectId = trip?.projectId ?? trip?.chantierId;
  if (projectId == null) return null;
  return (
    (projects || []).find((row) => Number(row.id) === Number(projectId)) ?? null
  );
}

/** Drop grid slots that no longer match persisted trip state. */
export function pruneOrphanTripSlots(planning, trip) {
  if (!trip?.id) return planning || [];
  if (trip.date && tripHasAssignedTechnician(trip)) {
    return planning || [];
  }
  return (planning || []).filter((slot) => slot.tripId !== trip.id);
}

export const pruneOrphanInterventionSlots = pruneOrphanTripSlots;
export const pruneCiPlanningEntries = pruneOrphanTripSlots;

function groupIncludesTechnician(group, technicianId) {
  const slotId = Number(technicianId);
  if (Number.isNaN(slotId)) return false;
  return (readGroupField(group, 'technician_ids') || []).some(
    (id) => id != null && Number(id) === slotId,
  );
}

function samePlanningTechnicianId(left, right) {
  if (left == null || right == null) return false;
  return Number(left) === Number(right);
}

/** Whether a planning slot should render on the week grid. */
export function isPlanningSlotVisible(slot, { trips }) {
  if (!slot?.date || slot.technicianId == null) return false;
  if (!slot.tripId) return false;
  const trip = findTripForSlot(trips, slot);
  if (trip?.isImportedFromBigchange) return false;
  if (!trip) {
    // Schedule row exists but trip list not loaded yet (e.g. dispo drop before sidebar).
    return !(trips || []).length;
  }
  if (!trip.date || trip.date !== slot.date) return false;
  return (trip.groups || []).some((group) =>
    groupIncludesTechnician(group, slot.technicianId),
  );
}

/** Drop orphan / stale rows that would block the grid without rendering. */
export function sanitizePlanningEntries(planning, { trips }) {
  return (planning || []).filter((slot) =>
    isPlanningSlotVisible(slot, { trips }),
  );
}

export function visiblePlanningForDay(
  technicianId,
  dateStr,
  { planning, trips, interventions } = {},
) {
  const tripList = trips ?? interventions ?? [];
  return (planning || []).filter(
    (slot) =>
      samePlanningTechnicianId(slot.technicianId, technicianId) &&
      slot.date === dateStr &&
      isPlanningSlotVisible(slot, { trips: tripList }),
  );
}

/** Replace trip-backed slots in the date range; keep out-of-range rows. */
export function mergeScheduleIntoPlanning(
  existing,
  fromApi,
  from,
  to,
  { trips } = {},
) {
  const kept = (existing || []).filter((slot) => {
    if (trips?.length && !isPlanningSlotVisible(slot, { trips })) {
      return false;
    }
    if (!slot.tripId) return true;
    if (!slot.date) return false;
    if (slot.date < from || slot.date > to) return true;
    return false;
  });
  let merged = [...kept, ...fromApi];
  if (!trips?.length) {
    return merged;
  }

  merged = sanitizePlanningEntries(merged, { trips });
  const covered = new Set(
    merged
      .filter((slot) => slot.tripId != null)
      .map((slot) => `${slot.tripId}:${slot.date}:${slot.technicianId}`),
  );

  const extraSlots = trips.flatMap((trip) => {
    if (
      !trip?.date ||
      trip.date < from ||
      trip.date > to ||
      !tripHasAssignedTechnician(trip)
    ) {
      return [];
    }
    return planningSlotsFromTrip(trip).filter((slot) => {
      const key = `${slot.tripId}:${slot.date}:${slot.technicianId}`;
      if (covered.has(key)) return false;
      covered.add(key);
      return true;
    });
  });
  merged = [...merged, ...extraSlots];

  return sanitizePlanningEntries(merged, { trips });
}

export const tripApiToRow = tripFromApi;
export const interventionApiToRow = tripFromApi;

/* ---------- Compatibility shims (legacy SAV/VT + chantier APIs) ---------- */

/** Split projects payload into project rows and flat trips. */
export function projectsResponseToPlanningData(projectsData) {
  const projects = normalizePlanningList(projectsData).map(projectFromApi);
  const trips = projects.flatMap((project) =>
    project.interventions.flatMap((intervention) => intervention.trips),
  );
  return { projects, interventions: trips, trips };
}

/** @deprecated Use projectsResponseToPlanningData. Returns `{ chantiers, interventions }` shape for legacy callers. */
export function chantiersResponseToPlanningData(data) {
  const { projects, trips } = projectsResponseToPlanningData(data);
  return { chantiers: projects, interventions: trips };
}

/** @deprecated */
export function chantiersResponseToDb(data) {
  const { chantiers, interventions } = chantiersResponseToPlanningData(data);
  return { chantiers, ci: interventions };
}

/** @deprecated VT/SAV are now Interventions; fetch projects/interventions instead. */
export async function fetchPlanningAnnexVisits() {
  return {
    technicalVisits: [],
    afterSalesVisits: [],
    contacts: [],
    sites: [],
  };
}

export function annexVisitFromApi(data) {
  return { ...(data || {}), id: data?.id };
}

export function contactFromPlanningApi(data) {
  return {
    id: data?.id,
    nom: data?.nom || '',
    prenom: data?.prenom || '',
    gender: data?.gender ?? null,
  };
}

export function siteFromPlanningApi(data) {
  return {
    id: data?.id,
    adresse: data?.adresse || '',
    city: data?.city || '',
    latitude: data?.latitude ?? null,
    longitude: data?.longitude ?? null,
  };
}

/** @deprecated VT/SAV now create Interventions with the right domain. */
export async function createAnnexVisitPlanning() {
  throw new Error(
    'createAnnexVisitPlanning is removed; create an Intervention (domain=vt|sav) on a Project then a Trip.',
  );
}

/** @deprecated SAV/VT trip mutations now go through patchTrip. */
export async function patchAnnexVisitPlanning() {
  throw new Error('patchAnnexVisitPlanning is removed; use patchTrip.');
}

/** @deprecated */
export async function clearAnnexVisitPlanning() {
  throw new Error(
    'clearAnnexVisitPlanning is removed; use patchTrip({clear_planning:true}).',
  );
}

/** Toggle planning verification for an intervention layer (updates its linked client). */
export async function setInterventionPlanningVerified(
  interventionId,
  isVerified,
) {
  return patchInterventionLayer(interventionId, { is_verified: isVerified });
}

/** @deprecated Use setInterventionPlanningVerified */
export async function setChantierPlanningVerified(interventionId, isVerified) {
  return setInterventionPlanningVerified(interventionId, isVerified);
}

export function annexScheduleEntryId(visitId, technicianId) {
  return stableCrc32Id(`annex:${visitId}:${technicianId}`);
}

export function planningSlotsFromAnnexVisit() {
  return [];
}

export function mergeAnnexVisitSlotsIntoPlanning(planning, _visit) {
  return planning || [];
}

/** Maps a technician from the API into the planning technicians row shape. */
export function sortTechniciansByGridOrder(technicians) {
  return [...technicians].sort((a, b) => {
    const orderDiff = Number(a.gridRowOrder ?? 0) - Number(b.gridRowOrder ?? 0);
    if (orderDiff !== 0) return orderDiff;
    const lastName = String(a.nom || '').localeCompare(
      String(b.nom || ''),
      'fr',
    );
    if (lastName !== 0) return lastName;
    return Number(a.id) - Number(b.id);
  });
}

export function technicianApiToRow(data) {
  return {
    id: data.id,
    prenom: data.first_name,
    nom: data.last_name,
    email: data.email || '',
    gridRowOrder: data.grid_row_order ?? 0,
    taskSkills: data.task_skills || [],
    affinities: data.affinities || [],
    permanent: data.is_active,
    addr: data.address || '',
    latitude: data.latitude,
    longitude: data.longitude,
    hierarchicalLevel: data.hierarchical_level || '',
    isJoker: Boolean(data.is_joker),
    contractType: data.contract_type || '',
    isInternal: Boolean(data.is_internal),
    scheduledHierarchicalLevel: data.scheduled_hierarchical_level || '',
    scheduledIsJoker: Boolean(data.scheduled_is_joker),
    scheduledRoleDate: data.scheduled_role_date || null,
    imageUrl: data.image_url || '',
    photo: data.image_url || '',
    guest: Boolean(data.guest),
    hiddenFromPlanning: Boolean(data.hidden_from_planning),
    companyName: data.company_name || '',
  };
}

export function guestTechnicianFromApi(data) {
  return {
    ...technicianApiToRow(data),
    guest: data.guest !== false,
    hiddenFromPlanning: Boolean(data.hidden_from_planning),
    companyName: data.company_name || '',
  };
}

export async function fetchTechnicianDirectory(ids) {
  const unique = [
    ...new Set(
      (ids || [])
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value > 0),
    ),
  ];
  if (!unique.length) return [];
  const response = await api.get('/planning/technicians/directory/', {
    ids: unique.join(','),
  });
  return normalizePlanningList(response.data).map(guestTechnicianFromApi);
}

export async function fetchPlanningTechniciansFromApi() {
  const response = await api.get('/planning/technicians/');
  return sortTechniciansByGridOrder(
    normalizePlanningList(response.data).map(technicianApiToRow),
  );
}

export async function patchPlanningTechnician(id, payload) {
  const response = await api.patch(`/planning/technicians/${id}/`, {}, payload);
  return technicianApiToRow(response.data);
}

export async function patchTechnicianAffinities(technicianId, affinities) {
  const response = await api.patch(
    `/planning/technicians/${technicianId}/affinities/`,
    {},
    { affinities },
  );
  return technicianApiToRow(response.data);
}

export async function fetchPlanningTechnicianCandidates() {
  const response = await api.get('/planning/technicians/candidates/');
  return normalizePlanningList(response.data).map(technicianApiToRow);
}

export async function moveTechnicianGridRow(technicianId, direction) {
  const response = await api.post(
    `/planning/technicians/${technicianId}/move-grid-row/`,
    {},
    { direction },
  );
  return sortTechniciansByGridOrder(
    normalizePlanningList(response.data).map(technicianApiToRow),
  );
}

export async function reorderTechnicianGridRows(technicianIds) {
  const response = await api.post(
    '/planning/technicians/reorder-grid/',
    {},
    { technician_ids: technicianIds },
  );
  return sortTechniciansByGridOrder(
    normalizePlanningList(response.data).map(technicianApiToRow),
  );
}

function timefoldProposalFromApi(row) {
  return {
    id: row.id,
    tripId: row.trip_id,
    interventionId: row.intervention_id,
    projectId: row.project_id,
    label: row.label || '',
    date: row.date || '',
    groups: row.groups || [],
    technicianIds: row.technician_ids || [],
    startTime: row.start_time || '',
    endTime: row.end_time || '',
    status: row.status,
    isPinnedInput: Boolean(row.is_pinned_input),
    rawTrip: row.trip || null,
  };
}

export function formatTimefoldScore(score) {
  const text = String(score || '').trim();
  if (!text) return '';
  const match = text.match(/(-?\d+)\s*hard\s*\/\s*(-?\d+)\s*soft/i);
  if (!match) return text;
  return `Hard ${match[1]} · Soft ${match[2]}`;
}

export function timefoldRunIsReviewable(status) {
  return status === 'ready' || status === 'confirmed_partial';
}

export function timefoldRunFromApi(data) {
  if (!data) return null;
  return {
    id: data.id,
    dateFrom: data.date_from,
    dateTo: data.date_to,
    technicianId: data.technician_id ?? null,
    status: data.status,
    score: data.score || '',
    error: data.error || '',
    solverSettings: data.solver_settings || null,
    createdDate: data.created_date,
    proposals: (data.proposals || []).map(timefoldProposalFromApi),
    projects: (data.projects || []).map(projectFromApi),
    interventionLayers: (data.intervention_layers || []).map(
      interventionLayerFromApi,
    ),
    pinnedCount: data.pinned_count || 0,
    proposedCount: data.proposed_count || 0,
    plannedInterventionsTotalHt:
      data.planned_interventions_total_ht == null
        ? null
        : Number(data.planned_interventions_total_ht) || 0,
    plannedInterventionsTotalResidual:
      data.planned_interventions_total_residual == null
        ? null
        : Number(data.planned_interventions_total_residual) || 0,
    plannedInterventionsTotalResidualLines:
      data.planned_interventions_total_residual_lines == null
        ? null
        : residualLinesFromApi(data.planned_interventions_total_residual_lines),
  };
}

export function ghostTripFromTimefoldProposal(proposal) {
  return {
    ...tripFromApi({
      ...(proposal.rawTrip || {}),
      id: -proposal.id,
      date: proposal.date,
      groups: proposal.groups,
      label: proposal.label || proposal.rawTrip?.label,
    }),
    isTimefoldGhost: true,
    proposalId: proposal.id,
    sourceTripId: proposal.tripId ?? null,
  };
}

export function ghostSlotsFromTimefoldProposal(proposal) {
  const ghostTrip = ghostTripFromTimefoldProposal(proposal);
  const slots = [];
  (ghostTrip.groups || []).forEach((group, groupIndex) => {
    const startTime = readGroupField(group, 'start_time') || proposal.startTime;
    const endTime = readGroupField(group, 'end_time') || proposal.endTime;
    (readGroupField(group, 'technician_ids') || []).forEach(
      (technicianId, slotIndex) => {
        if (technicianId == null) return;
        slots.push({
          id: `tf-${proposal.id}-${technicianId}-${groupIndex}-${slotIndex}`,
          tripId: ghostTrip.id,
          interventionId: ghostTrip.id,
          interventionLayerId: ghostTrip.interventionId,
          projectId: ghostTrip.projectId || proposal.projectId,
          technicianId: Number(technicianId),
          date: ghostTrip.date,
          startTime,
          endTime,
          note: '',
          isTimefoldGhost: true,
          proposalId: proposal.id,
          sourceTripId: proposal.tripId ?? null,
        });
      },
    );
  });
  return slots;
}

export async function createTimefoldOptimizerRun(
  dateFrom,
  dateTo,
  { technicianId } = {},
) {
  const response = await api.post(
    '/planning/optimizer/runs/',
    {},
    {
      date_from: dateFrom,
      date_to: dateTo,
      ...(technicianId != null ? { technician_id: technicianId } : {}),
    },
  );
  return timefoldRunFromApi(response.data);
}

export async function fetchTimefoldOptimizerRun(runId) {
  const response = await api.get(`/planning/optimizer/runs/${runId}/`);
  return timefoldRunFromApi(response.data);
}

export async function fetchTimefoldOptimizerHistory(
  dateFrom,
  dateTo,
  technicianId,
) {
  const response = await api.get('/planning/optimizer/runs/history/', {
    date_from: dateFrom,
    date_to: dateTo,
    ...(technicianId != null ? { technician_id: technicianId } : {}),
  });
  const rows = Array.isArray(response.data) ? response.data : [];
  return rows.map((row) => ({
    id: row.id,
    dateFrom: row.date_from,
    dateTo: row.date_to,
    technicianId: row.technician_id ?? null,
    status: row.status,
    score: row.score || '',
    createdDate: row.created_date,
    openProposalCount: Number(row.open_proposal_count) || 0,
  }));
}

export async function confirmTimefoldOptimizerRun(runId, proposalIds) {
  const response = await api.post(
    `/planning/optimizer/runs/${runId}/confirm/`,
    {},
    { proposal_ids: proposalIds },
  );
  return response.data;
}

export async function rejectTimefoldOptimizerRun(runId, proposalIds) {
  const payload = proposalIds == null ? {} : { proposal_ids: proposalIds };
  const response = await api.post(
    `/planning/optimizer/runs/${runId}/reject/`,
    {},
    payload,
  );
  return timefoldRunFromApi(response.data);
}

export async function clearTimefoldWeekPlanning(dateFrom, dateTo) {
  const response = await api.post(
    '/planning/optimizer/runs/clear-week/',
    {},
    { date_from: dateFrom, date_to: dateTo },
  );
  return response.data;
}

export function planningAlertFromApi(data) {
  return {
    id: data.id,
    clientId: data.client_id ?? null,
    interventionId: data.intervention_id ?? null,
    reason: data.reason || '',
    projectId: data.project_id ?? null,
    clientName: data.client_name || '',
    serviceTypeSlug: data.service_type_slug || '',
    serviceTypeLabel: data.service_type_label || '',
    domainLabel: data.domain_label || '',
  };
}

export async function fetchPlanningAlerts({ refresh = true } = {}) {
  const params = refresh ? {} : { refresh: 0 };
  const response = await api.get('/planning/alerts/', params);
  const data = response.data || {};
  return {
    show: Boolean(data.show),
    alerts: (data.alerts || []).map(planningAlertFromApi),
  };
}

export async function dismissPlanningAlertsToday() {
  await api.post('/planning/alerts/dismiss/', {}, {});
}
